import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, AlertTriangle, XCircle, RefreshCw, CheckCircle, AlertCircle,
  Clock, Sparkles, FileText, User, Calendar, TrendingUp, ChevronDown,
  ChevronUp, History, Eye, UserCheck, MessageSquare, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDarkMode } from '../../context/DarkModeContext';
import ConflictReviewModal from './ConflictReviewModal';
import ConflictStatusBadge from './ConflictStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConflictTabView = ({ caseId }) => {
  const { isDarkMode } = useDarkMode();
  const [status, setStatus] = useState(null);
  const [allRuns, setAllRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);

  const fetchConflictData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Get current status
      const statusRes = await axios.get(`${API}/conflicts/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(statusRes.data);
      
      // Get all runs from DB
      const runsRes = await axios.get(`${API}/conflicts/${caseId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      
      setAllRuns(runsRes.data || []);
      
    } catch (error) {
      console.error('Failed to fetch conflict data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConflictData();
  }, [caseId]);

  const handleManualCheck = async () => {
    setChecking(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/conflicts/check/${caseId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Konfliktsjekk fullført');
      await fetchConflictData();
    } catch (error) {
      toast.error('Konfliktsjekk feilet');
      console.error(error);
    } finally {
      setChecking(false);
    }
  };

  const getStatusConfig = () => {
    const configs = {
      CLEAR: {
        icon: Shield,
        color: 'emerald',
        title: 'Ingen interessekonflikt',
        description: 'Saken er sjekket og ingen potensielle konflikter er identifisert.'
      },
      PENDING_REVIEW: {
        icon: AlertTriangle,
        color: 'amber',
        title: 'Krever vurdering',
        description: 'AI har funnet potensielle konflikter som må vurderes av advokat.'
      },
      CONFLICT: {
        icon: XCircle,
        color: 'red',
        title: 'Interessekonflikt bekreftet',
        description: 'Det foreligger en dokumentert interessekonflikt i denne saken.'
      },
      ESCALATED: {
        icon: AlertCircle,
        color: 'purple',
        title: 'Eskalert til partner/admin',
        description: 'Saken er sendt til partner eller admin for videre vurdering.'
      }
    };
    return configs[status?.status] || configs.CLEAR;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const config = getStatusConfig();
  const Icon = config.icon;
  const hasMatches = status?.latest_run?.total_matches > 0;

  return (
    <>
      <div className="space-y-6">
        {/* Header Section */}
        <div className={`rounded-2xl border p-6 ${
          isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-start gap-4 flex-1">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                isDarkMode 
                  ? `bg-${config.color}-900/30` 
                  : `bg-${config.color}-100`
              }`}>
                <Icon className={`w-7 h-7 ${
                  isDarkMode 
                    ? `text-${config.color}-400` 
                    : `text-${config.color}-600`
                }`} />
              </div>
              <div className="flex-1">
                <h2 className={`text-2xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {config.title}
                </h2>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {config.description}
                </p>
                {status?.latest_run && (
                  <div className={`flex items-center gap-2 mt-3 text-xs ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    <Clock className="w-3.5 h-3.5" />
                    Siste sjekk: {new Date(status.latest_run.created_at).toLocaleString('nb-NO')}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleManualCheck}
              disabled={checking}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
              Kjør ny sjekk
            </button>
          </div>

          {/* Current Status Summary */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl ${
            isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'
          }`}>
            <div>
              <div className={`text-xs font-medium mb-1 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Totalt sjekket
              </div>
              <div className={`text-2xl font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {allRuns.length || 0}
              </div>
            </div>
            <div>
              <div className={`text-xs font-medium mb-1 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Potensielle konflikter
              </div>
              <div className={`text-2xl font-bold ${
                hasMatches 
                  ? 'text-amber-500' 
                  : isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
              }`}>
                {status?.latest_run?.total_matches || 0}
              </div>
            </div>
            <div>
              <div className={`text-xs font-medium mb-1 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Status
              </div>
              <ConflictStatusBadge status={status?.status} compact={false} />
            </div>
          </div>
        </div>

        {/* Latest Run Details */}
        {status?.latest_run && hasMatches && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl border p-6 ${
              isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold flex items-center gap-2 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <Sparkles className="w-5 h-5 text-amber-500" />
                AI-funn fra siste sjekk
              </h3>
              {status.status === 'PENDING_REVIEW' && (
                <button
                  onClick={() => setShowReviewModal(true)}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium hover:shadow-lg transition-all"
                >
                  Vurder konflikter
                </button>
              )}
            </div>

            <div className="space-y-3">
              {status.latest_run.matches_found.map((match, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`p-4 rounded-xl border ${
                    isDarkMode 
                      ? 'bg-gray-900/50 border-gray-700' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <User className={`w-4 h-4 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                      <span className={`font-semibold ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {match.matched_entity.name}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {match.matched_entity.role}
                      </span>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      match.confidence === 'HIGH' 
                        ? isDarkMode 
                          ? 'bg-red-900/30 text-red-400' 
                          : 'bg-red-100 text-red-700'
                        : match.confidence === 'MEDIUM'
                        ? isDarkMode
                          ? 'bg-amber-900/30 text-amber-400'
                          : 'bg-amber-100 text-amber-700'
                        : isDarkMode
                        ? 'bg-gray-700 text-gray-400'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {match.confidence === 'HIGH' ? 'Høy tillit' : match.confidence === 'MEDIUM' ? 'Medium tillit' : 'Lav tillit'}
                    </span>
                  </div>

                  <div className={`mb-3 p-3 rounded-lg ${
                    isDarkMode ? 'bg-gray-800/50' : 'bg-white'
                  }`}>
                    <div className="flex items-start gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className={`text-xs font-medium mb-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          AI-forklaring:
                        </div>
                        <div className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {match.explanation}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`flex items-center gap-4 text-xs ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    <div className="flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      Match i: <span className="font-medium">{match.matched_in_case_title}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Zap className="w-3.5 h-3.5" />
                      Type: <span className="font-medium">{match.conflict_type}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Review Section */}
        {status?.latest_review && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl border p-6 ${
              isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <UserCheck className="w-5 h-5 text-emerald-500" />
              Siste vurdering
            </h3>
            <div className="flex items-start gap-4">
              <CheckCircle className={`w-6 h-6 flex-shrink-0 ${
                isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
              }`} />
              <div className="flex-1">
                <div className={`font-semibold mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Beslutning: {
                    status.latest_review.decision === 'CLEAR' ? 'Godkjent - ingen konflikt' 
                    : status.latest_review.decision === 'CONFLICT' ? 'Konflikt bekreftet' 
                    : 'Eskalert til vurdering'
                  }
                </div>
                <div className={`text-sm mb-2 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {status.latest_review.rationale_text}
                </div>
                <div className={`text-xs flex items-center gap-2 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(status.latest_review.reviewed_at).toLocaleString('nb-NO')}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Audit Log / History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl border ${
            isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-bold flex items-center gap-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <History className="w-5 h-5" />
              Full logg og historikk
            </h3>
            <p className={`text-sm mt-1 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Alle konfliktsjekker og vurderinger for denne saken
            </p>
          </div>
          
          {allRuns.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {allRuns.map((run, idx) => (
                <div key={run.run_id} className="p-6">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedRun(expandedRun === run.run_id ? null : run.run_id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        run.total_matches > 0
                          ? isDarkMode ? 'bg-amber-900/30' : 'bg-amber-100'
                          : isDarkMode ? 'bg-emerald-900/30' : 'bg-emerald-100'
                      }`}>
                        {run.total_matches > 0 ? (
                          <AlertTriangle className={`w-5 h-5 ${
                            isDarkMode ? 'text-amber-400' : 'text-amber-600'
                          }`} />
                        ) : (
                          <Shield className={`w-5 h-5 ${
                            isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                          }`} />
                        )}
                      </div>
                      <div>
                        <div className={`font-semibold ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          Sjekk #{allRuns.length - idx}
                        </div>
                        <div className={`text-xs ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          {new Date(run.created_at).toLocaleString('nb-NO')} • {run.triggered_by}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${
                        run.total_matches > 0
                          ? isDarkMode ? 'text-amber-400' : 'text-amber-600'
                          : isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                      }`}>
                        {run.total_matches} funn
                      </span>
                      {expandedRun === run.run_id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedRun === run.run_id && run.matches_found?.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-4 space-y-2"
                      >
                        {run.matches_found.map((match, matchIdx) => (
                          <div 
                            key={matchIdx}
                            className={`p-3 rounded-lg text-sm ${
                              isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'
                            }`}
                          >
                            <div className={`font-medium mb-1 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              {match.matched_entity.name}
                            </div>
                            <div className={`text-xs ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {match.explanation}
                            </div>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <History className={`w-12 h-12 mx-auto mb-3 ${
                isDarkMode ? 'text-gray-600' : 'text-gray-300'
              }`} />
              <p className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Ingen konfliktsjekker registrert ennå
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Review Modal */}
      {showReviewModal && status?.latest_run && (
        <ConflictReviewModal
          caseId={caseId}
          runId={status.latest_run.run_id}
          matches={status.latest_run.matches_found}
          onClose={() => setShowReviewModal(false)}
          onSubmitted={() => {
            setShowReviewModal(false);
            fetchConflictData();
          }}
        />
      )}
    </>
  );
};

export default ConflictTabView;
